import { LayerTemplate, getLayerTypeLabel } from '@/types/layerTemplate';
import {
    Business as BusinessIcon,
    CalendarToday as CalendarIcon,
    Category as CategoryIcon,
    ContentCopy as CopyIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    Lock as LockIcon,
    MoreVert as MoreVertIcon,
    Person as PersonIcon,
    Public as PublicIcon
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';

interface LayerTemplateCardProps {
  template: LayerTemplate;
  onClick?: (template: LayerTemplate) => void;
  onEdit?: (template: LayerTemplate) => void;
  onCopy?: (template: LayerTemplate) => void;
  onDelete?: (template: LayerTemplate) => void;
  onExport?: (template: LayerTemplate) => void;
}

const LayerTemplateCard: React.FC<LayerTemplateCardProps> = ({
  template,
  onClick,
  onEdit,
  onCopy,
  onDelete,
  onExport
}) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = () => {
    if (onClick) {
      onClick(template);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) {
      onEdit(template);
    }
  };

  const handleCopy = () => {
    handleMenuClose();
    if (onCopy) {
      onCopy(template);
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) {
      onDelete(template);
    }
  };

  const handleExport = () => {
    handleMenuClose();
    if (onExport) {
      onExport(template);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const layerType = getLayerTypeLabel(template.type);

  // Visibility status UUIDs
  const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
  const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
  const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

  // Determine visibility status
  const visibilityStatusId = template.visibility_status_id;
  const isPublic = visibilityStatusId === PUBLIC_STATUS_UUID;
  const isCompanyPublic = visibilityStatusId === COMPANY_PUBLIC_UUID;
  const isPrivate = !visibilityStatusId || visibilityStatusId === PRIVATE_UUID;

  // Check if user can edit/delete this template
  const isFromUserOrg = template.organization_id === user?.organization_id;
  const canEdit = isFromUserOrg || (isPublic && isSuperAdmin);
  const canDelete = isFromUserOrg || (isPublic && isSuperAdmin);
  const canCopy = true;
  const canExport = true;
  const canShowMenu = canCopy || canExport || canEdit || canDelete;

  const getVisibilityChip = () => {
    if (isPublic) {
      return {
        icon: <PublicIcon />,
        label: 'Public',
        color: 'success' as const
      };
    } else if (isCompanyPublic) {
      return {
        icon: <BusinessIcon />,
        label: 'Company Public',
        color: 'info' as const
      };
    } else {
      return {
        icon: <LockIcon />,
        label: 'Private',
        color: 'default' as const
      };
    }
  };

  const visibilityChip = getVisibilityChip();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: onClick ? 4 : 1,
        }
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Action Menu Button */}
        {canShowMenu && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            {template.name}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={visibilityChip.icon}
              label={visibilityChip.label}
              size="small"
              color={visibilityChip.color}
              variant="outlined"
            />
            <Chip
              icon={<CategoryIcon />}
              label={layerType}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {template.description?.trim() && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {template.description.trim()}
            </Typography>
          )}
        </Box>

        {/* Author and Editor */}
        <Box sx={{ mb: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              Author: <strong>{template.author_name || 'Unknown'}</strong>
            </Typography>
          </Box>

          {template.editor_name && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'success.main' }}>
                <EditIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                Editor: <strong>{template.editor_name}</strong>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Dates */}
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">
              Created: <strong>{formatDate(template.created_at)}</strong>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
            <Typography variant="caption" color="text.secondary">
              Updated: <strong>{formatDate(template.updated_at)}</strong>
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {canCopy && (
          <MenuItem onClick={handleCopy}>
            <CopyIcon fontSize="small" sx={{ mr: 1 }} />
            Copy
          </MenuItem>
        )}
        {canExport && (
          <MenuItem onClick={handleExport}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Export CSV
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default LayerTemplateCard;
